import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mic, Zap } from 'lucide-react';
import { FetchDeviceQuery, FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { FetchFullStreamQuery } from '@/lib/api/monitoring/alert';
import { useCurrentTime } from '@/lib/time';
import { LivePlayer } from '@/components/Playback/LivePlayer';
import { PushToTalkTab } from '@/app/Alert/components/PushToTalkTab';
import { RelayTab } from '@/app/Alert/components/RelayTab';
import { AlertEventsContextProvider } from '@/app/Alert/AlertEventsProvider';
import { getVideoProcessorId } from '../utils/utils';
import { Spinner } from '@/components/ui/spinner';

const noop = () => {};

/** Minimal wrapper around the real AlertEventsContext so PushToTalkTab/RelayTab
 *  can call useAlertEventsCtx() without the full AlertEventsProvider. */
function StubAlertEventsProvider({ children }: { children: React.ReactNode }) {
  return (
    <AlertEventsContextProvider
      selectedEventId={undefined}
      nextEventId={undefined}
      prevEventId={undefined}
      nextUnviewedEventId={undefined}
      unviewedCount={0}
      totalEvents={0}
      selectedEvent={undefined}
      events={[] as any}
      setSelectedEventId={noop}
      prev={noop}
      next={noop}
      nextUnviewed={noop}
      first={noop}
      last={noop}
      mode="all"
      setMode={noop}
    >
      {children}
    </AlertEventsContextProvider>
  );
}

export interface LiveTesterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  cameraId: string;
}

export function LiveTesterDialog({ open, onOpenChange, deviceId, cameraId }: LiveTesterDialogProps) {
  // Fetch device data
  const deviceQuery = useQuery({
    ...FetchDeviceQuery({ deviceId }),
    enabled: open && !!deviceId,
  });
  const device = deviceQuery.data;

  // Fetch sub-devices to resolve camera info
  const subDevicesQuery = useQuery({
    ...FetchSubDevicesQuery({ deviceId }),
    enabled: open && !!deviceId,
  });
  const camera = useMemo(
    () => subDevicesQuery.data?.find((c) => c.id === cameraId),
    [subDevicesQuery.data, cameraId]
  );

  const companyId = device?.customerId ?? '';
  const projectId = device?.projectId ?? '';

  // Fetch live stream - gated properly like the alert page does
  const now = useCurrentTime(60 * 5 * 1000);
  const canStream = open && !!companyId && !!projectId && !!cameraId;
  const streamQuery = useQuery({
    ...FetchFullStreamQuery({
      occurred: now,
      companyId,
      projectId,
      videoProcessorIdentifier: cameraId,
    }),
    staleTime: 30 * 1000,
    enabled: canStream,
  });

  const stream = streamQuery.data;
  const [liveConfig, setLiveConfig] = useState({
    url: stream?.liveUrl,
    urlType: stream?.liveUrlType,
    isLoading: true,
  });

  useEffect(() => {
    if (!stream) return;
    setLiveConfig({
      url: stream.liveUrl,
      urlType: stream.liveUrlType,
      isLoading: false,
    });
  }, [stream]);

  // Reset loading state when dialog opens with new camera
  useEffect(() => {
    if (open) {
      setLiveConfig({ url: undefined, urlType: undefined, isLoading: true });
    }
  }, [open, cameraId]);

  const [activePanel, setActivePanel] = useState<'talk' | 'relays'>('talk');

  const interactionTabProps = {
    projectId,
    companyId,
    useAlertEvents: false,
    videoProcessorId: getVideoProcessorId(camera),
  };

  const isLoading = deviceQuery.isLoading || !device;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-full xl:max-w-350 w-full h-[80vh] p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Live Tester</DialogTitle>
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-4">
            <p className="text-lg font-bold text-gray-500">Loading Device</p>
            <Spinner size="large" className="text-gray-500" />
          </div>
        ) : (
          <div className="grid h-full grid-cols-[1fr_320px]">
            {/* Live player */}
            <div className="relative flex flex-1 overflow-hidden">
              <LivePlayer
                companyId={companyId}
                isLiveUrlLoading={liveConfig.isLoading}
                liveUrl={liveConfig.url ?? undefined}
                liveUrlType={liveConfig.urlType}
                projectId={projectId}
                videoProcessorIdentifier={cameraId}
              />
            </div>

            {/* Interaction panel */}
            <div className="flex h-full flex-col overflow-y-auto border-l border-[#2E3835]">
              <StubAlertEventsProvider>
                <Tabs value={activePanel} onValueChange={(v) => setActivePanel(v as any)} className="w-full">
                  <TabsList className="h-10 w-full rounded-none border-x-0 border-b border-t-0 border-[#2E3835] z-20 bg-[#1b1b1b]">
                    <TabsTrigger value="talk" className="h-10 px-3 text-xs">
                      <Mic className="mr-1 h-3 w-3" />
                      Talk
                    </TabsTrigger>
                    <TabsTrigger value="relays" className="h-10 px-3 text-xs">
                      <Zap className="mr-1 h-3 w-3" />
                      Relays
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="talk" className="mt-0 border-b">
                    <PushToTalkTab {...interactionTabProps} />
                  </TabsContent>
                  <TabsContent value="relays" className="mt-0 border-b">
                    <RelayTab {...interactionTabProps} />
                  </TabsContent>
                </Tabs>
              </StubAlertEventsProvider>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
